import Link from "next/link";
import {
  AppWindowMac,
  ArrowLeftToLine,
  ArrowRightToLine,
  Database,
  LayoutGrid,
  ListChecks,
  Logs,
  MessagesSquare,
  Server,
  Settings
} from "lucide-react";

import { NavItem } from "@/types";
import { useRouter } from "next/router";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { OptionalTooltip } from "@/components/OptionalTooltip";
import { Icon } from "@/components/landing/Navbar";

export const menu = {
  dashboard: { title: "Dashboard", href: "/dashboard", icon: LayoutGrid },
  chat: {
    title: "AI Agent",
    href: "/chat",
    icon: MessagesSquare
  },
  host: { title: "Servers", href: "/host", icon: Server },
  app: { title: "Apps", href: "/app", icon: AppWindowMac },
  task: { title: "Tasks", href: "/task", icon: ListChecks },
  journal: { title: "History", href: "/journal", icon: Logs },
  backup: { title: "Backups", href: "/backup", icon: Database }
} as const satisfies Record<string, NavItem>;

const sidebarClassNames = cva(
  "group fixed h-[100vh] left-0 z-10 hidden flex-col bg-sidebar overflow-hidden sm:flex border-r",
  {
    variants: {
      collapsed: {
        true: "w-14",
        false: "w-44"
      }
    },
    defaultVariants: {
      collapsed: false
    }
  }
);

const navItemClassNames = cva(
  "flex items-center flex h-10 min-w-10 gap-1 px-2 rounded-md border border-transparent " +
    "active:scale-95 transition-all",
  {
    variants: {
      active: {
        true: "bg-primary text-primary-foreground",
        false:
          "text-muted-foreground hover:text-foreground hover:border-border hover:bg-sidebar-accent"
      },
      collapsed: {
        true: "justify-center",
        false: "justify-start"
      }
    }
  }
);

interface SidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const router = useRouter();

  const handleToggleCollapse = () => {
    onToggle(!collapsed);
  };

  return (
    <aside className={sidebarClassNames({ collapsed })}>
      <nav className="flex flex-col px-2 gap-2 pt-[12px]">
        <Link href="/#stay" title="DollarDeploy" className="font-bold flex items-center gap-2">
          {collapsed ? (
            <Icon className="h-full w-full px-2 pt-[2px] pb-1" />
          ) : (
            <img
              src="/logo-main.svg"
              className="h-7 pl-2 py-1 dark:invert-100"
              alt="DollarDeploy"
            />
          )}
        </Link>

        {Object.entries(menu).map(([_name, item]) => (
          <OptionalTooltip key={item.title} tooltip={item.title} show={collapsed}>
            <Link
              href={item.href}
              className={navItemClassNames({
                active: router.pathname?.startsWith(item.href),
                collapsed
              })}
            >
              <span className="flex items-center justify-center h-6 w-6">
                {typeof item.icon === "string" ? (
                  <img src={item.icon} alt={item.title} className="h-5 w-5" />
                ) : (
                  <item.icon className="h-5 w-5" />
                )}
              </span>
              {!collapsed && <span className="text-sm ml-1">{item.title}</span>}
            </Link>
          </OptionalTooltip>
        ))}
      </nav>

      <nav className="flex flex-col gap-2 px-2 pb-4 mt-auto">
        <OptionalTooltip tooltip="Settings" show={collapsed}>
          <Link
            href="/settings"
            className={navItemClassNames({
              active: router.pathname === "/settings",
              collapsed
            })}
          >
            <span className="flex items-center justify-center h-6 w-6">
              <Settings className="h-5 w-5" />
            </span>
            {!collapsed && <span className="text-sm">Settings</span>}
          </Link>
        </OptionalTooltip>

        <OptionalTooltip tooltip="Expand" show={collapsed}>
          <Link
            href="#"
            onClick={handleToggleCollapse}
            className={cn(
              navItemClassNames({
                active: false,
                collapsed
              }),
              "text-muted-foreground/60 group-hover:text-muted-foreground"
            )}
          >
            <span className="flex items-center justify-center h-6 w-6">
              {collapsed ? (
                <ArrowRightToLine className="h-5 w-5" />
              ) : (
                <ArrowLeftToLine className="h-5 w-5" />
              )}
            </span>
            {!collapsed && <span className="text-sm">Collapse</span>}
          </Link>
        </OptionalTooltip>
      </nav>
    </aside>
  );
}
